import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import AOS from "aos";
import "aos/dist/aos.css";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../axiosInstance";
import { Link, useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import AdminDashboardLayout from "./AdminDashboardLayout";
import * as bootstrap from "bootstrap";

const NewsManagementPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";
  const [newsList, setNewsList] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedNews, setSelectedNews] = useState(null);
  const [filteredNewsTypes, setFilteredNewsTypes] = useState([]);

  const [stats, setStats] = useState({
    totalNewsCount: 0,
    totalViewsCount: 0,
    totalCategoriesCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("categories");
  const [editingNews, setEditingNews] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedNewsTypeId, setSelectedNewsTypeId] = useState("");
  const [searchKeyword, setSearchKeyword] = useState("");
  const [newsTypes, setNewsTypes] = useState([]);
  const [selectedTypeId, setSelectedTypeId] = useState(null);
  const [formData, setFormData] = useState({
    arabicTitle: "",
    englishTitle: "",
    arabicDescription: "",
    englishDescription: "",
    newsTypeId: "",
    publishToPublic: false,
    ThumbnailImagePath: null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // تحميل الأخبار عند التحميل
  useEffect(() => {
    fetchNews(1, selectedNewsTypeId, searchKeyword);
    fetchNewsTypes();
  }, []);

  const fetchNews = async (page = 1, category = "", keyword = "") => {
    setLoading(true);
    try {
      const response = await axiosInstance.get("/api/v1/admins/news", {
        headers: { "Accept-Language": "ar-SA" },
        params: {
          page,
          category: category === "all" ? "" : category,
          keyword,
        },
      });
      setNewsList(response.data.items);
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "حدث خطأ أثناء جلب الخبر";
      toast.error(errorMessage);
      console.error("فشل في جلب الخبر", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchNewsTypes = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/admins/newstypes", {
        headers: { "Accept-Language": "ar-SA" },
      });
      setNewsTypes(response.data.items);
    } catch (err) {
      console.error("Error fetching news types", err);
    }
  };

  const addNews = async (data) => {
    try {
      const payload = {
        arabicTitle: data.arabicTitle,
        englishTitle: data.englishTitle,
        arabicDescription: data.arabicDescription,
        englishDescription: data.englishDescription,
        newsTypeId: data.newsTypeId,
        publishToPublic: data.publishToPublic,
        ThumbnailImagePath: data.ThumbnailImagePath, // يمكن أن يكون null
      };

      const formDataToSend = new FormData();
      for (const key in payload) {
        if (payload[key] !== null && payload[key] !== undefined) {
          formDataToSend.append(key, payload[key]);
        }
      }

      await axiosInstance.post("/api/v1/admins/news", formDataToSend, {
        headers: {
          "Accept-Language": "ar-SA",
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("تمت إضافة الخبر بنجاح");
      fetchNews();
    } catch (err) {
      const message =
        err.response?.data?.detail || err.response?.data?.title || err.message;
      toast.error(message || "حدث خطأ أثناء رفع الخبر");
      setError(err.response?.data || err.message);
    }
  };

  const updateNews = async (id, data) => {
    try {
      const payload = {
        arabicTitle: data.arabicTitle,
        englishTitle: data.englishTitle,
        arabicDescription: data.arabicDescription,
        englishDescription: data.englishDescription,
        newsTypeId: data.newsTypeId,
        publishToPublic: data.publishToPublic,
        ThumbnailImagePath: data.ThumbnailImagePath, // صورة مصغرة جديدة (اختيارية)
      };

      const formDataToSend = new FormData();
      for (const key in payload) {
        if (payload[key] !== null && payload[key] !== undefined) {
          formDataToSend.append(key, payload[key]);
        }
      }

      await axiosInstance.put(`/api/v1/admins/news/${id}`, formDataToSend, {
        headers: {
          "Accept-Language": "ar-SA",
          "Content-Type": "multipart/form-data",
        },
      });

      toast.success("تم تعديل الخبر بنجاح");
      fetchNews();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "حدث خطأ أثناء تعديل الخبر";
      toast.error(errorMessage);
      console.error("فشل في تعديل الخبر", error);
    }
  };

  const deleteNews = async (id) => {
    try {
      await axiosInstance.delete(`/api/v1/admins/news/${id}`);
      setNewsList((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "حدث خطأ أثناء حذف الخبر";
      toast.error(errorMessage);
      console.error("فشل في حذف الخبر", error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "file" ? files[0] : type === "checkbox" ? checked : value,
    }));

    if (type === "file" && files[0]) {
      const reader = new FileReader();
      reader.onload = () => {
        if (imagePreviewRef.current) {
          imagePreviewRef.current.src = reader.result;
          imagePreviewRef.current.classList.remove("d-none");
        }
      };
      reader.readAsDataURL(files[0]);
    }
  };

  const resetForm = () => {
    setFormData({
      arabicTitle: "",
      englishTitle: "",
      arabicDescription: "",
      englishDescription: "",
      newsTypeId: "",
      publishToPublic: false,
      ThumbnailImagePath: null,
    });
    setEditingNews(null);

    if (imagePreviewRef.current) {
      imagePreviewRef.current.src = "";
      imagePreviewRef.current.classList.add("d-none");
    }
  };

  const handleSaveNews = () => {
    if (editingNews) {
      updateNews(editingNews.id, formData);
    } else {
      addNews(formData);
    }

    const modal = bootstrap.Modal.getInstance(
      document.getElementById("newsModal")
    );
    modal.hide();
    resetForm();
  };

  const handleEditClick = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      arabicTitle: newsItem.arabicTitle || "",
      englishTitle: newsItem.englishTitle || "",
      arabicDescription: newsItem.arabicDescription || "",
      englishDescription: newsItem.englishDescription || "",
      newsTypeId: String(newsItem.newsTypeId || ""),
      publishToPublic: !!newsItem.publishToPublic,
      ThumbnailImagePath: "",
    });

    // عرض الصورة القديمة في المعاينة
    if (imagePreviewRef.current) {
      imagePreviewRef.current.src = newsItem.thumbnailImagePath || "";
      imagePreviewRef.current.classList.remove("d-none");
    }

    const modal = new bootstrap.Modal(document.getElementById("newsModal"));
    modal.show();
  };

  // Get all news types
  const getAllNewsTypes = async () => {
    try {
      const response = await axiosInstance.get("/api/v1/admins/newstypes");
      console.log("News Types Response:", response.data);

      const types = response.data; // البيانات موجودة هنا
      console.log("Fetched types:", types);
      setNewsTypes(types.items || []);
    } catch (error) {
      console.error("فشل في جلب الأنواع", error);
    }
  };

  // Get a single news type by ID
  const getNewsTypeById = async (id) => {
    try {
      const response = await axiosInstance.get(
        `/api/v1/admins/newstypes/${id}`
      );
      setFormData({
        arabicName: response.data.arabicName,
        englishName: response.data.englishName,
      });
      setSelectedTypeId(id);
    } catch (error) {
      console.error("فشل في جلب النوع", error);
    }
  };

  // Create a new news type
  const createNewsType = async () => {
    try {
      await axiosInstance.post("/api/v1/admins/newstypes", formData);
      toast.success("تم إنشاء النوع بنجاح");
      setFormData({ arabicName: "", englishName: "" });
      getAllNewsTypes();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "حدث خطأ أثناء إنشاء النوع";
      toast.error(errorMessage);
      console.error("فشل في إنشاء النوع", error);
    }
  };

  // Update a news type
  const updateNewsType = async () => {
    try {
      await axiosInstance.put(
        `/api/v1/admins/newstypes/${selectedTypeId}`,
        formData
      );
      setFormData({ arabicName: "", englishName: "" });
      setSelectedTypeId(null);
      getAllNewsTypes();
    } catch (error) {
      const errorMessage =
        error.response?.data?.detail ||
        error.response?.data?.title ||
        error.message ||
        "حدث خطأ أثناء تعديل النوع";
      toast.error(errorMessage);
      console.error("فشل في تعديل النوع", error);
    }
  };

  // Delete a news type
  const deleteNewsType = async (id) => {
    if (window.confirm("هل أنت متأكد من الحذف؟")) {
      try {
        await axiosInstance.delete(`/api/v1/admins/newstypes/${id}`);
        getAllNewsTypes();
      } catch (error) {
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.title ||
          error.message ||
          "حدث خطأ أثناء حذف النوع";
        toast.error(errorMessage);
        console.error("فشل في حذف النوع", error);
      }
    }
  };

  // تحميل البيانات عند أول تحميل
  useEffect(() => {
    getAllNewsTypes();
  }, []);

  // دالة إرسال نوع الخبر
  const handleSubmit = (e) => {
    e.preventDefault();
    if (selectedTypeId) {
      updateNewsType();
    } else {
      createNewsType();
    }
  };

  const imagePreviewRef = useRef(null);

  const handleTabClick = (tab) => {
    setActiveTab(tab);
    setSelectedNewsTypeId(tab);
    setCurrentPage(1);
    fetchNews(1, tab, searchKeyword);
  };

  const handleSearchChange = (e) => {
    const keyword = e.target.value;
    setSearchKeyword(keyword);
    setCurrentPage(1);
    fetchNews(1, selectedNewsTypeId, keyword);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchNews(newPage, selectedNewsTypeId, searchKeyword);
  };

  useEffect(() => {
    AOS.init();
    fetchNews();
  }, []);

  useEffect(() => {
    // فلترة newsTypes حسب searchTerm
    if (!searchTerm) {
      setFilteredNewsTypes(newsTypes);
    } else {
      const filtered = newsTypes.filter(
        (type) =>
          type.arabicName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          type.englishName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredNewsTypes(filtered);
    }
  }, [searchTerm, newsTypes]);

  useEffect(() => {
    fetchNewsTypes();
  }, []);

  useEffect(() => {
    fetchNews(1, selectedNewsTypeId, searchKeyword);
  }, [selectedNewsTypeId, searchKeyword]);

  return (
    <AdminDashboardLayout pageTitle="إدارة الأخبار" dir={direction}>
      {/* العنوان ومسارات التصفح */}
      <div className="row mb-4">
        <div className="col-md-12">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h2 className="mb-1">{t("newsManagement")}</h2>
              <nav aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <a href="#">{t("dashboard")}</a>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {t("News")}
                  </li>
                </ol>
              </nav>
            </div>
            <div>
              <button
                className="btn btn-primary-custom"
                data-bs-toggle="modal"
                data-bs-target="#newsModal"
                onClick={() => setEditingNews(null)}
              >
                <i className="fas fa-plus-circle me-2"></i>
                <span>{t("addNews")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* بطاقات الإحصائيات */}
      <div className="row mb-4">
        {/* عدد الأخبار */}
        <div className="col-sm-6 col-xl-4 mb-3">
          <div className="stat-card">
            <div className="icon bg-primary-custom text-white">
              <i className="fas fa-newspaper"></i>
            </div>
            <div className="number">{stats.totalNewsCount}</div>
            <div className="label">{t("stats.totalNews")}</div>
            <div className="progress mt-3" style={{ height: "5px" }}>
              <div
                className="progress-bar bg-primary-custom"
                style={{ width: "100%" }}
              ></div>
            </div>
          </div>
        </div>

        {/* عدد المشاهدات */}
        <div className="col-sm-6 col-xl-4 mb-3">
          <div className="stat-card">
            <div className="icon bg-success text-white">
              <i className="fas fa-eye"></i>
            </div>
            <div className="number">{stats.totalViewsCount}</div>
            <div className="label">{t("stats.totalViews")}</div>
            <div className="progress mt-3" style={{ height: "5px" }}>
              <div
                className="progress-bar bg-success"
                style={{ width: "100%" }}
              ></div>
            </div>
          </div>
        </div>

        {/* عدد التصنيفات */}
        <div className="col-sm-6 col-xl-4 mb-3">
          <div className="stat-card">
            <div className="icon bg-secondary-custom text-white">
              <i className="fas fa-folder-open"></i>
            </div>
            <div className="number">{stats.totalCategoriesCount}</div>
            <div className="label">{t("stats.totalCategories")}</div>
            <div className="progress mt-3" style={{ height: "5px" }}>
              <div
                className="progress-bar bg-secondary-custom"
                style={{ width: "100%" }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/*  News Management Tabs  */}
      <div className="card shadow-sm border-0 mb-4">
        <div className="card-body p-0">
          <ul className="nav nav-tabs border-bottom-0" role="tablist">
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link text-dark ${
                  activeTab === "all-news" ? "active" : ""
                }`}
                onClick={() => handleTabClick("all-news")}
                type="button"
                role="tab"
              >
                <i className="fas fa-th-list me-2"></i>
                <span>{t("tabs.allNews")}</span>
              </button>
            </li>
            <li className="nav-item" role="presentation">
              <button
                className={`nav-link text-dark ${
                  activeTab === "categories" ? "active" : ""
                }`}
                onClick={() => handleTabClick("categories")}
                type="button"
                role="tab"
              >
                <i className="fas fa-tags me-2"></i>
                <span>{t("tabs.categories")}</span>
              </button>
            </li>
          </ul>

          <div className="tab-content bg-white p-4">
            {/* جدول الاخبار */}
            {activeTab === "all-news" && (
              <div className="tab-pane fade show active">
                <div className="row mb-4">
                  <div className="col-md-6 mb-3 mb-md-0">
                    {newsTypes.length > 0 && (
                      <select
                        className="form-select"
                        value={selectedNewsTypeId}
                        onChange={(e) => setSelectedNewsTypeId(e.target.value)}
                      >
                        <option value="all">{t("Filter.allCategories")}</option>{" "}
                        {newsTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.arabicName}
                          </option>
                        ))}
                      </select>
                    )}
                  </div>

                  <div className="col-md-6">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t("searchs.news")}
                        value={searchKeyword}
                        onChange={(e) => setSearchKeyword(e.target.value)}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() =>
                          fetchNews(1, selectedNewsTypeId, searchKeyword)
                        }
                      >
                        <i className="fas fa-search"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive mt-4">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th width="70">#</th>
                        <th>{t("table.title")}</th>
                        <th width="120">{t("table.category")}</th>
                        <th width="130">{t("table.date")}</th>
                        <th width="100" className="text-center">
                          {t("table.views")}
                        </th>
                        <th width="120">{t("table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="6" className="text-center">
                            {t("table.loading")}
                          </td>
                        </tr>
                      ) : (
                        newsList
                          .filter((news) => news.publishToPublic)
                          .map((news, index) => (
                            <tr key={news.id}>
                              <td>{index + 1}</td>
                              <td>{news.arabicTitle}</td>
                              <td>{news.newsType?.arabicName}</td>
                              <td>
                                {new Date(news.createdOn).toLocaleDateString(
                                  "ar-EG"
                                )}
                              </td>
                              <td className="text-center">{news.viewCount}</td>
                              <td>
                                <i
                                  className="fas fa-edit text-primary me-3 cursor-pointer"
                                  onClick={() => handleEditClick(news)}
                                  title={t("Actions.edit")}
                                  style={{ cursor: "pointer" }}
                                ></i>

                                <i
                                  className="fas fa-trash-alt text-danger"
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        t("Actions.confirmDeleteNews")
                                      )
                                    ) {
                                      deleteNews(news.id);
                                    }
                                  }}
                                  title={t("Actions.delete")}
                                  style={{ cursor: "pointer" }}
                                ></i>
                              </td>
                            </tr>
                          ))
                      )}
                      {!loading &&
                        newsList.filter((news) => news.publishToPublic)
                          .length === 0 && (
                          <tr>
                            <td colSpan="6" className="text-center">
                              {t("table.noNews")}
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            {/* جدول التخصصات */}

            {activeTab === "categories" && (
              <div className="tab-pane fade show active">
                <div className="row mb-4">
                  <div className="col-md-6 mb-3 mb-md-0">
                    <button
                      className="btn btn-primary-custom"
                      data-bs-toggle="modal"
                      data-bs-target="#addCategoryModal"
                    >
                      <i className="fas fa-plus-circle me-2"></i>
                      <span>{t("Actions.addCategory")}</span>
                    </button>
                  </div>

                  <div className="col-md-6">
                    <div className="input-group">
                      <input
                        type="text"
                        className="form-control"
                        placeholder={t("searchs.categories")}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => {
                          fetchNews(1, selectedNewsTypeId, searchTerm);
                        }}
                      >
                        <i className="fas fa-search"></i>
                      </button>
                    </div>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th width="70">#</th>
                        <th>{t("table.category")}</th>
                        <th width="130">{t("table.color")}</th>
                        <th width="100" className="text-center">
                          {t("table.newsCount")}
                        </th>
                        <th width="100">{t("table.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredNewsTypes.length === 0 ? (
                        <tr>
                          <td colSpan="5" className="text-center">
                            {t("table.noCategories")}
                          </td>
                        </tr>
                      ) : (
                        filteredNewsTypes.map((type, index) => (
                          <tr key={type.id}>
                            <td>{index + 1}</td>
                            <td>
                              {type.arabicName} / {type.englishName}
                            </td>
                            <td>
                              <div className="d-flex align-items-center">
                                <div
                                  className="color-preview me-2"
                                  style={{
                                    width: "20px",
                                    height: "20px",
                                    backgroundColor: type.color || "#ccc",
                                    borderRadius: "4px",
                                  }}
                                ></div>
                                <span>
                                  {type.color || t("general.noColor")}
                                </span>
                              </div>
                            </td>
                            <td className="text-center">
                              {type.newsCount || 0}
                            </td>
                            <td>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => {
                                  getNewsTypeById(type.id);
                                  document
                                    .getElementById("openAddCategoryModalBtn")
                                    ?.click();
                                }}
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger ms-1"
                                onClick={() => deleteNewsType(type.id)}
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* مودل اضافة التصنيفات */}
      <div
        className="modal fade"
        id="addCategoryModal"
        tabIndex="-1"
        aria-labelledby="addCategoryModalLabel"
        aria-modal="true"
        role="dialog"
      >
        <div className="modal-dialog">
          <form onSubmit={handleSubmit}>
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id="addCategoryModalLabel">
                  {t("categories.modalTitle")}
                </h5>
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  {t("common.cancel")}
                </button>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label htmlFor="name" className="form-label">
                    {t("categories.name")}
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="color" className="form-label">
                    {t("categories.color")}
                  </label>
                  <input
                    type="color"
                    className="form-control form-control-color w-100"
                    id="color"
                    value={formData.color}
                    onChange={handleChange}
                    title={t("categories.colorTooltip")}
                    required
                  />
                  <small className="text-muted">
                    {t("categories.colorNote")}
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  onClick={resetForm}
                >
                  {t("common.cancel")}
                </button>
                <button type="submit" className="btn btn-primary-custom">
                  {t("common.add")}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/*  مودال الخبر للإضافة والتعديل */}
      <div className="modal fade" id="newsModal" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {editingNews ? t("news.editTitle") : t("news.addTitle")}
              </h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                onClick={resetForm}
              ></button>
            </div>
            <div className="modal-body">
              <form>
                <div className="mb-3">
                  <label className="form-label">{t("news.titleAr")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="arabicTitle"
                    value={formData.arabicTitle}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.titleEn")}</label>
                  <input
                    type="text"
                    className="form-control"
                    name="englishTitle"
                    value={formData.englishTitle}
                    onChange={handleChange}
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.type")}</label>
                  <select
                    className="form-select"
                    name="newsTypeId"
                    value={formData.newsTypeId || ""}
                    onChange={handleChange}
                  >
                    <option value="">{t("news.selectType")}</option>
                    {newsTypes.map((type) => (
                      <option key={type.id} value={String(type.id)}>
                        {type.arabicName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {t("news.descriptionAr")}
                  </label>
                  <textarea
                    className="form-control"
                    name="arabicDescription"
                    value={formData.arabicDescription}
                    onChange={handleChange}
                  ></textarea>
                </div>
                <div className="mb-3">
                  <label className="form-label">
                    {t("news.descriptionEn")}
                  </label>
                  <textarea
                    className="form-control"
                    name="englishDescription"
                    value={formData.englishDescription}
                    onChange={handleChange}
                  ></textarea>
                </div>
                <div className="mb-3 form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="publishToPublic"
                    checked={formData.publishToPublic}
                    onChange={handleChange}
                  />
                  <label className="form-check-label">
                    {t("news.publish")}
                  </label>
                </div>
                <div className="mb-3">
                  <label className="form-label">{t("news.thumbnail")}</label>
                  <input
                    type="file"
                    className="form-control"
                    name="ThumbnailImagePath"
                    accept="image/*"
                    onChange={handleChange}
                  />
                  <img
                    ref={imagePreviewRef}
                    src=""
                    alt="Preview"
                    className="img-fluid mt-2 rounded d-none"
                    style={{ maxHeight: "150px" }}
                  />
                </div>
              </form>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                data-bs-dismiss="modal"
                onClick={resetForm}
              >
                {t("common.cancel")}
              </button>
              <button
                type="button"
                className="btn btn-primary-custom"
                onClick={handleSaveNews}
              >
                {t("common.save")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ToastContainer position="top-right" autoClose={5000} />
    </AdminDashboardLayout>
  );
};

export default NewsManagementPage;
